export const DEFAULT_ROAD_FACTOR = 1.25;

export function haversineKm(origin, destination) {
  if (!origin || !destination) return null;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const latDelta = toRadians(destination.lat - origin.lat);
  const lngDelta = toRadians(destination.lng - origin.lng);
  const originLat = toRadians(origin.lat);
  const destinationLat = toRadians(destination.lat);
  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(originLat) * Math.cos(destinationLat) * Math.sin(lngDelta / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function estimateTrip({
  distanceKm,
  travelDistanceKm,
  oneWayRoadDistanceKm,
  tripMode = "roundTrip",
  pumpPrice,
  budgetEuro,
  consumptionLPer100Km,
  roadFactor = DEFAULT_ROAD_FACTOR,
}) {
  const values = [distanceKm, pumpPrice, budgetEuro, consumptionLPer100Km, roadFactor];
  if (values.some((value) => !Number.isFinite(value)) || pumpPrice <= 0 || budgetEuro <= 0) {
    return null;
  }

  const oneWayRoadKm = Number.isFinite(oneWayRoadDistanceKm)
    ? Math.max(0, oneWayRoadDistanceKm)
    : Math.max(0, distanceKm) * roadFactor;
  const travelKm = Number.isFinite(travelDistanceKm)
    ? Math.max(0, travelDistanceKm)
    : oneWayRoadKm * (tripMode === "oneWay" ? 1 : 2);
  const travelLitres = (travelKm * Math.max(0, consumptionLPer100Km)) / 100;
  const travelCost = travelLitres * pumpPrice;
  const purchasedLitres = budgetEuro / pumpPrice;
  const effectivePrice = (budgetEuro + travelCost) / purchasedLitres;

  return {
    oneWayRoadKm,
    travelKm,
    roundTripKm: travelKm,
    tripMode,
    travelLitres,
    travelCost,
    purchasedLitres,
    totalOutlay: budgetEuro + travelCost,
    effectivePrice,
  };
}

export function rankStations(stations, fuel, options) {
  const roadFactor = Number.isFinite(options.roadFactor) ? options.roadFactor : DEFAULT_ROAD_FACTOR;
  return stations
    .map((station) => {
      const pumpPrice = station.prices?.[fuel];
      const distanceKm = haversineKm(options.origin, station);
      let travelDistanceKm;
      let oneWayRoadDistanceKm;
      const roadMetric = options.roadMetrics?.get?.(station.id);
      if (options.requireRoadMetrics && !roadMetric) return null;
      if (roadMetric) {
        oneWayRoadDistanceKm = roadMetric.oneWayKm;
        travelDistanceKm = options.tripMode === "enRoute"
          ? roadMetric.detourKm
          : roadMetric.oneWayKm * (options.tripMode === "oneWay" ? 1 : 2);
      }
      if (!roadMetric && options.tripMode === "enRoute") {
        if (!options.destination) return null;
        const directRouteKm = haversineKm(options.origin, options.destination) * roadFactor;
        const viaStationKm =
          (distanceKm + haversineKm(station, options.destination)) * roadFactor;
        travelDistanceKm = Math.max(0, viaStationKm - directRouteKm);
      }
      const estimate = estimateTrip({
        ...options,
        roadFactor,
        distanceKm,
        travelDistanceKm,
        oneWayRoadDistanceKm,
        pumpPrice,
      });
      return estimate ? { ...station, pumpPrice, distanceKm, estimate } : null;
    })
    .filter(Boolean)
    .filter((station) =>
      options.tripMode === "enRoute"
        ? station.estimate.travelKm <= options.radiusKm
          && (!Number.isFinite(options.remainingRangeKm)
            || options.remainingRangeKm <= 0
            || station.estimate.oneWayRoadKm <= options.remainingRangeKm)
        : options.requireRoadMetrics
          ? station.estimate.oneWayRoadKm <= options.radiusKm
          : station.distanceKm <= options.radiusKm,
    )
    .sort(
      (left, right) =>
        left.estimate.effectivePrice - right.estimate.effectivePrice ||
        left.distanceKm - right.distanceKm,
    );
}
